import { AuthRequiredError, RequestError } from '../sync/api.js';
import { getDeviceId } from '../sync/db.js';
import { completePhoto, initPhoto, putChunk } from './api.js';
import {
  markFailed,
  markRetryable,
  markUploaded,
  markUploading,
  nextPhoto,
  setProgress,
} from './queue.js';
import type { PhotoRecord } from './types.js';

/**
 * Загрузка фото кусками с докачкой.
 *
 * Сервер сам говорит, какие куски у него уже есть, — после обрыва досылаются
 * только недостающие. Перезаливать файл целиком на мобильном интернете в цеху
 * значит не загрузить его никогда.
 */

export type PhotoUploadOutcome = 'uploaded' | 'retry' | 'failed' | 'idle';

function missingChunks(total: number, received: number[]): number[] {
  const have = new Set(received);
  const result: number[] = [];
  for (let index = 0; index < total; index += 1) {
    if (!have.has(index)) result.push(index);
  }
  return result;
}

export async function uploadPhoto(record: PhotoRecord): Promise<PhotoUploadOutcome> {
  const deviceId = await getDeviceId();

  try {
    const state = await initPhoto(
      {
        id: record.id,
        reportId: record.reportId,
        kind: record.kind,
        siteId: record.siteId,
        machineId: record.machineId,
        shiftNumber: record.shiftNumber,
        takenAt: record.takenAt,
        bytes: record.bytes,
        width: record.width,
        height: record.height,
        sha256: record.sha256,
        chunkSize: record.chunkSize,
      },
      deviceId,
    );

    // Файл уже собран на сервере — например, подтверждение потерялось в прошлый раз
    if (state.uploaded) {
      await markUploaded(record.id);
      return 'uploaded';
    }

    if (!record.blob) {
      await markFailed(record.id, 'Снимок потерян на устройстве');
      return 'failed';
    }

    await markUploading(record.id, {
      chunkSize: state.chunkSize,
      totalChunks: state.totalChunks,
      uploadedChunks: state.receivedChunks.length,
    });

    let uploaded = state.receivedChunks.length;
    for (const index of missingChunks(state.totalChunks, state.receivedChunks)) {
      const start = index * state.chunkSize;
      const chunk = record.blob.slice(start, Math.min(start + state.chunkSize, record.bytes));
      const progress = await putChunk(record.id, index, chunk, deviceId);
      uploaded = progress.receivedChunks.length;
      await setProgress(record.id, uploaded);
    }

    await completePhoto(record.id, deviceId);
    await markUploaded(record.id);
    return 'uploaded';
  } catch (error) {
    // Сессия кончилась — снимок остаётся на устройстве и уйдёт после входа
    if (error instanceof AuthRequiredError) {
      await markRetryable(record.id, error.message);
      return 'retry';
    }
    if (error instanceof RequestError) {
      await markFailed(record.id, error.message);
      return 'failed';
    }
    const message = error instanceof Error ? error.message : String(error);
    await markRetryable(record.id, message);
    return 'retry';
  }
}

/**
 * Разобрать очередь фотографий. Останавливаемся на первой сетевой ошибке:
 * если связи нет для одного снимка, её нет и для остальных.
 */
export async function runPhotoQueue(maxPhotos = 20): Promise<number> {
  let done = 0;
  for (let i = 0; i < maxPhotos; i += 1) {
    const record = await nextPhoto();
    if (!record) break;

    const outcome = await uploadPhoto(record);
    if (outcome === 'uploaded') done += 1;
    if (outcome === 'retry') break;
  }
  return done;
}
